import { Alert, 
         Box, 
         Card, 
         Chip, 
         MenuItem, 
         FormControl, 
         Select, 
         Snackbar, 
         Tooltip, 
         Typography, 
         useTheme, 
         InputLabel, 
         FormControlLabel, 
         Checkbox 
       }                                        from "@mui/material"
import { DataGridPro, 
         DataGridProProps, 
         GridActionsCellItem, 
         GridCellParams, 
         GridColDef, 
         GridHeaderFilterCellProps, 
         GridRenderCellParams, 
         GridRenderEditCellParams, 
         GridRowId, 
         useGridApiRef 
       }                                        from "@mui/x-data-grid-pro";
import { SurveyQBApis }                           from "apis/surveyQBAPI";
import { BglCloseOutlinedSmallButton, 
         BglPrimaryOutlinedSmallButton, 
         BglPrimarySmallButton, 
         BglSecondaryOutlinedSmallButton 
       }                                        from "components/button/BglButtons";
import { useGlobalConfig }                      from "context/GlobalContext";
import { valueToArrayFilterOperator }           from "features/recommendations/home/CustomFilterOperators";
import { MultiChoiceFilterComponent, SingleChoiceFilterComponent }          from "features/recommendations/home/CustomHeaderFilters";
import { getRecoTableColumnsStyles }            from "features/recommendations/home/RecoTableColumnsStyles";
import { getChipStyles, getRecoTableStyles }    from "features/recommendations/home/RecoTableStyles";
import { cloneDeep, isEqual }                              from "lodash";
import { LucideLibrary, 
         CirclePlus,
         EditIcon 
       }                                        from "lucide-react";  
import React, { 
       CSSProperties, 
       useEffect, 
       useMemo, 
       useState 
       }                                        from "react";
import { useDispatch, 
         useSelector 
       }                                        from "react-redux";
import { CMS_DEFAULT_LOCALE, 
         selectCMSLocales, 
         selectCMSSelectedPilot 
       }                                        from "store/dataReducers/cmsPilotSlice";
import { selectDraftQuestions, 
         selectModifiedQuestions, 
         selectPublishedQuestions, 
         selectHasUnsavedRows, 
         selectUnsavedRows, 
         setShowOnlyUnsavedAction, 
         selectShowOnlyUnsaved, 
         selectTableState, 
         setSelectedLocaleAction, 
         discardUnsavedRowAction, 
         updateModifiedQuestionsAction, 
         discardAllDraftQuestionsAction, 
         selectReviewWorkflow, 
         publishQuestionsAction, 
         unpublishQuestionsAction, 
         setUnsavedRowAction, 
         setFiltersAction, 
         clearFiltersAction,
         selectSurveyCategories
       }                                        from "store/dataReducers/surveyQBSlice";
import { APIData, CONTENT_VERIFICATION_STATUS }                              from "types/cms/frontendTypes";
import SaveIcon                                 from '@mui/icons-material/Save';
import RestoreIcon                              from '@mui/icons-material/Restore';
import PublishIcon                              from '@mui/icons-material/Publish';
import FileDownloadOffIcon                      from '@mui/icons-material/FileDownloadOff';
import DeleteIcon                               from '@mui/icons-material/Delete';
import TuneIcon                                 from '@mui/icons-material/Tune';
import { SurveyQBUtils }                        from "../utils/SurveyQBUtils";
import { VerificationWorkflowUtils }            from "framework/utility/VerificationWorkflowUtils";
import { CONTENT_EDIT_ACTION, 
         CONTENT_EDIT_STATUS 
       }                                        from "types/cms/frontendTypes";
import { UpsertSurveyQuestion }                 from "./UpsertSurveyQuestion";
import { SurveyQuestionLocalization }           from "types/apis/surveyInterfaces";
import Textarea                                 from "@mui/joy/Textarea";

export const SurveyQuestionBank = () => {

  const { rc } = useGlobalConfig()

  const theme = useTheme()
  const apiRef = useGridApiRef();
  const dispatch = useDispatch()
  
  const styles = getRecoTableColumnsStyles(theme);
  const chipStyles = getChipStyles(theme);

  const [isSaving, setIsSaving]             = useState(false)
  const [selectedRows, setSelectedRows]     = useState<any[]>([])
  const [snackbar, setSnackbar]             = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'success' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'error'
  });
  const [dialogOpen, setDialogOpen]         = useState(false)
  const [dialogMode, setDialogMode]         = useState<'create' | 'edit'>('create')
  const [selectedQuestion, setSelectedQuestion] = useState<any>(null)
  const [selectedPublishedQuestion, setSelectedPublishedQuestion] = useState<any>(null)

  const selectedPilot     = useSelector(selectCMSSelectedPilot)!
  const locales           = useSelector(selectCMSLocales)!
  
  const categories          = useSelector(selectSurveyCategories)
  const draftQuestions      = useSelector(selectDraftQuestions)
  const publishedQuestions  = useSelector(selectPublishedQuestions)
  const modifiedQuestions   = useSelector(selectModifiedQuestions)
  
  const unsavedRows       = useSelector(selectUnsavedRows)
  const hasUnsavedRows    = useSelector(selectHasUnsavedRows)
  const tableState        = useSelector(selectTableState)
  const showOnlyUnsaved   = useSelector(selectShowOnlyUnsaved)
  const reviewWorkflow    = useSelector(selectReviewWorkflow)!
  
  const memoizedRows = useMemo(() => {

    const publishedQuestionsWithoutModifications = publishedQuestions.filter(
      pubQuestion => modifiedQuestions.find(modQuestion => modQuestion.documentId === pubQuestion.documentId) === undefined)
    
    const allRows = [...draftQuestions, ...modifiedQuestions, ...publishedQuestionsWithoutModifications]
    
    // Filter for unsaved rows only if toggle is active
    if (showOnlyUnsaved) {

      const rows = allRows.filter(row => unsavedRows[row.id] !== undefined)
      if (rows.length === 0) {
        dispatch(setShowOnlyUnsavedAction(false))
        return allRows
      }
      return rows
    }
    
    return allRows

  } ,[draftQuestions, modifiedQuestions, publishedQuestions, showOnlyUnsaved, unsavedRows, dispatch])

  // Initial page visit
  useEffect(() => {

    const supportedLocales = selectedPilot.supportedLocales as string[];
    if (supportedLocales && supportedLocales.length > 0) {
      const preferred = supportedLocales[0];
      if (preferred !== tableState.selectedLocale) {
        dispatch(setSelectedLocaleAction(preferred));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {

    Object.values(unsavedRows).forEach((unsavedRow) => {
      const originalRow = memoizedRows.find(r => r.documentId === unsavedRow.documentId)
      if (originalRow) {
        apiRef.current?.updateRows([{...originalRow, ...unsavedRow}]);
      }
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef, memoizedRows])

  const setMessage = React.useCallback((message : string, severity : 'error' | 'success' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message: message,
      severity: severity
    })
  }, [setSnackbar]);

  const discardSelectedUnsavedRows = React.useCallback(() => {
    const selectedRowIds = selectedRows.map(row => row.id);
    
    // Filter unsaved rows to only include selected ones
    const selectedUnsavedRows = Object.values(unsavedRows).filter(row => 
      selectedRowIds.includes(row.id)
    );

    // Discard from table
    selectedUnsavedRows.forEach((row) => {
      const originalRow = memoizedRows.find(r => r.documentId === row.documentId)
      if (originalRow) {
        apiRef.current?.updateRows([originalRow]);
      }
    });
    
    // Discard from store - need to dispatch individual discard actions
    selectedUnsavedRows.forEach((row) => {
      dispatch(discardUnsavedRowAction({id: row.id as any}));
    });

  }, [dispatch, unsavedRows, apiRef, memoizedRows, selectedRows]);


  const discardUnsavedRow = React.useCallback((id : GridRowId) => {

    // Discard from table
    const unsavedRow = unsavedRows[id]
    const originalRow = memoizedRows.find(r => r.documentId === unsavedRow.documentId)
    if (originalRow) {
      apiRef.current?.updateRows([originalRow]);
    }
    // Discard from store
    dispatch(discardUnsavedRowAction({id : id as any}));

  }, [dispatch, unsavedRows, apiRef, memoizedRows]);

  const updateRows = React.useCallback((questions : APIData<'api::survey-question.survey-question'>[]) => {
    apiRef.current?.updateRows(questions);
  }, [apiRef]);

  const saveQuestions = React.useCallback(async (questions : Partial<APIData<'api::survey-question.survey-question'>>[]) => {

    setIsSaving(true);

    const resp = await SurveyQBApis.updateQuestions(rc, {
      utilityDocumentId : selectedPilot.documentId,
      pilotId : selectedPilot.pilotId,
      data : questions as any
    })    
    let succeeded = true

    if (resp.error || resp.data?.error) {
      let errorMsg = resp.error?.message || resp.data?.error
      if (errorMsg && typeof errorMsg === 'object') {
        errorMsg = JSON.stringify(errorMsg)
      }
      setMessage(errorMsg, 'error');
      succeeded = false
    } else {
      const updatedQuestions = resp.data!.questions
      updateRows(updatedQuestions);
      dispatch(updateModifiedQuestionsAction({
        questions : updatedQuestions
      }))
    }

    setIsSaving(false);
    return succeeded
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rc, apiRef, dispatch])


  const saveSelectedUnsavedRows = React.useCallback(async () => {
    const selectedRowIds = selectedRows.map(row => row.id);
    
    // Filter unsaved rows to only include selected ones
    const selectedUnsavedRows = Object.values(unsavedRows).filter(row => 
      selectedRowIds.includes(row.id)
    );

    if (selectedUnsavedRows.length === 0) return;

    setIsSaving(true);

    const resp = await SurveyQBApis.updateQuestions(rc, {
      utilityDocumentId : selectedPilot.documentId,
      pilotId : selectedPilot.pilotId,
      data : selectedUnsavedRows as any
    })
    let succeeded = true

    if (resp.error || resp.data?.error) {
      let errorMsg = resp.error?.message || resp.data?.error
      if (errorMsg && typeof errorMsg === 'object') {
        errorMsg = JSON.stringify(errorMsg)
      }
      setMessage(errorMsg, 'error');
      succeeded = false
    } else {
      const updatedQuestions = resp.data!.questions
      apiRef.current?.updateRows(updatedQuestions);
      dispatch(updateModifiedQuestionsAction({
        questions : updatedQuestions
      }))
    }

    setIsSaving(false);
    return succeeded
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rc, apiRef, dispatch, unsavedRows, selectedRows])

  const discardAllDrafts = React.useCallback(async (docIds : string[]) => {

    setIsSaving(true);
    
    const resp = await SurveyQBApis.discardDraftQuestions(rc, selectedPilot.pilotId, docIds)

    if (resp.error) {
      setMessage(resp.error.message || 'Error discarding drafts', 'error');
    } else {
      dispatch(discardAllDraftQuestionsAction(resp.data!))
    }

    setIsSaving(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rc, dispatch])


  const publishSelectedRows = React.useCallback(async () => {
    const selectedRowsToPublish = selectedRows.filter(reco => {
      const workflowState = VerificationWorkflowUtils.getNextWorkflowState(rc, reco.verificationStatus, reviewWorkflow)
      return VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.PUBLISH, workflowState)
    })

    if (selectedRowsToPublish.length === 0) return;

    publishQuestions(selectedRowsToPublish.map(reco => reco.documentId))

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef, reviewWorkflow, rc, selectedRows])


  const publishQuestions = React.useCallback(async (questionDocIds : string[]) => {

    setIsSaving(true);

    const resp = await SurveyQBApis.publishQuestions(rc, selectedPilot.documentId, questionDocIds)

    if (resp.error) {
      setMessage(resp.error.message || 'Error publishing questions', 'error');
    } else {
      const updatedQuestions = resp.data!
      dispatch(publishQuestionsAction(updatedQuestions))
    }

    setIsSaving(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rc, dispatch])

  const unpublishQuestions = React.useCallback(async (questionDocIds : string[]) => {

    setIsSaving(true);

    const resp = await SurveyQBApis.unpublishQuestions(rc, selectedPilot.documentId, questionDocIds)

    if (resp.error) {
      setMessage(resp.error.message || 'Error unpublishing questions', 'error');
    } else {
      const draftQuestions = resp.data!
      dispatch(unpublishQuestionsAction(draftQuestions))
    }

    setIsSaving(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rc, dispatch])

  const processRowUpdate = React.useCallback<NonNullable<DataGridProProps['processRowUpdate']>>(
    async (newRow : any, oldRow : any) => {
      
      const memoRow = memoizedRows.find(r => r.documentId === newRow.documentId)!
      
      // Special handling for verificationStatus changes - make immediate API call
      if (newRow.verificationStatus !== oldRow.verificationStatus) {
        const succeeded = await saveQuestions([{
          documentId : memoRow.documentId,
          verificationStatus : newRow.verificationStatus
        }])
        return succeeded ? newRow : oldRow
      }
    
      const recoDiff = SurveyQBUtils.getQuestionDiff(newRow, memoRow) as any      

      if (Object.keys(recoDiff).length > 0) {
        recoDiff.id = memoRow.id;
        recoDiff.documentId = memoRow.documentId;
        dispatch(setUnsavedRowAction({ id: memoRow.id as any, diff : recoDiff }));
      } else {
        dispatch(discardUnsavedRowAction({id: newRow.id}));
      }

      return newRow;

  }, [dispatch, memoizedRows, saveQuestions]);

  const onProcessRowUpdateError = React.useCallback<NonNullable<DataGridProProps['onProcessRowUpdateError']>>(
    (error : any) => {
      console.error(error)
    }, []
  )

  const getRowClassName = React.useCallback<NonNullable<DataGridProProps['getRowClassName']>>(
    ({ id }) => {

      const unsavedRow = unsavedRows[id];
      if (unsavedRow && Object.keys(unsavedRow).length > 0) {
        return 'row--edited';
      }
      return '';
  }, [unsavedRows]);

  // Hightlight if value is modified b/w modified version and published version
  const getCellClassName = React.useCallback<NonNullable<DataGridProProps['getCellClassName']>>(
    (params : GridCellParams) => {

      const currentRow = memoizedRows.find(r => r.id === params.id);
      if (!currentRow || (currentRow as any).editStatus !== CONTENT_EDIT_STATUS.MODIFIED) {
        return '';
      }

      // Find the published version of this recommendation
      const publishedVersion = publishedQuestions.find(r => 
        r.documentId === currentRow.documentId
      );
      if (!publishedVersion) {
        return '';
      }

      // Get the diff between current and published version
      const diff = SurveyQBUtils.getQuestionDiff(currentRow as any, publishedVersion as any) as any;

      const isLocalizationField = ['title', 'info'].includes(params.field)
      const isCMSDefaultLocale = tableState.selectedLocale === CMS_DEFAULT_LOCALE

      if (!isCMSDefaultLocale) {
        if (isLocalizationField) {
          const currentLocalization = (currentRow as any).localizations?.find((l : SurveyQuestionLocalization) => l.locale === tableState.selectedLocale)
          const publishedLocalization = (publishedVersion as any).localizations?.find((l : SurveyQuestionLocalization) => l.locale === tableState.selectedLocale)
          
          const currValue = currentLocalization ? currentLocalization[params.field] : undefined
          const pubValue = publishedLocalization ? publishedLocalization[params.field] : undefined

          if (!isEqual(currValue, pubValue)) {
            return 'cell--edited'
          } else {
            return ''
          }
        }
      } 
      
      if (Object.keys(diff).includes(params.field)) {
        return 'cell--edited';
      }
    
      return '';
  }, [memoizedRows, publishedQuestions, tableState.selectedLocale]);

  const isCellEditable = React.useCallback<NonNullable<DataGridProProps['isCellEditable']>>(
    (params : GridCellParams) => {

      if (params.field === 'verificationStatus') {
        return true
      }

      const workflowState = VerificationWorkflowUtils.getNextWorkflowState(rc, params.row.verificationStatus, reviewWorkflow)
      const isSaveAllowed = VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.SAVE_UPDATE, workflowState)
      
      if (!isSaveAllowed) {
        return false
      }

      return true
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reviewWorkflow]
  )

  const handleFilterModelChange = React.useCallback((model: any) => {
    dispatch(setFiltersAction(model.items || []));
  }, [dispatch]);

  // Handle locale change
  const handleLocaleChange = (newLocale: string) => {
    dispatch(setSelectedLocaleAction(newLocale));
  };  

  const clearFilters = React.useCallback(() => {
    dispatch(clearFiltersAction());
    // Update the DataGridPro's filter model
    apiRef.current?.setFilterModel({ items: [] });
  }, [dispatch, apiRef]);

  const handleToggleUnsavedOnly = React.useCallback(() => {
    dispatch(setShowOnlyUnsavedAction(!showOnlyUnsaved));
    // Reset to first page when switching views
    apiRef.current?.setPage(0);
  }, [dispatch, showOnlyUnsaved, apiRef]);

  const handleSelectionChange = React.useCallback((rowSelectionModel: any, details: any) => {
    const selectedRowsData = Array.from(apiRef.current?.getSelectedRows()?.values() || []);
    setSelectedRows(selectedRowsData);
  }, [apiRef]);

  const addNewQuestion = () => {
    setDialogMode('create');
    setSelectedQuestion(null);
    setSelectedPublishedQuestion(null);
    setDialogOpen(true);
  }

  const editQuestion = (event : any, row : APIData<'api::survey-question.survey-question'>) => {
    event.stopPropagation()
    
    const quesDocId = row.documentId;
    const draftQues = draftQuestions.find(q => q.documentId === quesDocId);
    const modifiedQues = modifiedQuestions.find(q => q.documentId === quesDocId);
    const publishedQues = publishedQuestions.find(q => q.documentId === quesDocId);

    setSelectedQuestion(draftQues || modifiedQues || publishedQues);
    setSelectedPublishedQuestion(publishedQues);

    setDialogMode('edit');
    setDialogOpen(true);
  }

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedQuestion(null);
    setSelectedPublishedQuestion(null);
  }

  // Helper function to get locale-specific value
  const getLocaleValue = (reco: APIData<"api::survey-question.survey-question">, fieldName: keyof SurveyQuestionLocalization, selectedLocale: string) => {
    // For English locale, return the main field value
    if (selectedLocale === CMS_DEFAULT_LOCALE) {
      return reco[fieldName] || '';
    }
    
    // For other locales, return the localizations value or fallback to main field
    const localizations: SurveyQuestionLocalization[] = (reco as any).localizations || [];
    const localeData = localizations.find((l: SurveyQuestionLocalization) => l.locale === selectedLocale) || {} as SurveyQuestionLocalization;
    return localeData[fieldName] || '';
  };

  // Helper function to set locale-specific value
  const setLocaleValue = (reco: APIData<"api::survey-question.survey-question">, fieldName: keyof SurveyQuestionLocalization, value: any, selectedLocale: string) => {
    const updatedReco = cloneDeep(reco)
    
    if (selectedLocale === CMS_DEFAULT_LOCALE) {
      updatedReco[fieldName] = value;
    } else {
      // For other locales, update the localizations array
      let localizations: SurveyQuestionLocalization[] = cloneDeep((reco as any).localizations || []);
      const existingIndex = localizations.findIndex((l: SurveyQuestionLocalization) => l.locale === selectedLocale);
      if (existingIndex >= 0) {
        // Update existing locale entry - create new array instead of mutating
        const existing = localizations[existingIndex];
        const updated: SurveyQuestionLocalization = {
          ...existing,
          [fieldName]: value,
        };
        const newLocalizations = [...localizations.slice(0, existingIndex), updated, ...localizations.slice(existingIndex + 1)];
        localizations = newLocalizations;
      } else {
        // Create new locale entry
        const newLocaleEntry: SurveyQuestionLocalization = {
          locale: selectedLocale,
          [fieldName]: value,
        } as SurveyQuestionLocalization;
        localizations.push(newLocaleEntry);
      }

      (updatedReco as any).localizations = localizations
    }
    
    return updatedReco;
  };

  const getTextColumn = (field : string, headerName : string, width : number, selectedLocale: string, maxLength?: number) : GridColDef => {
    // Only apply locale logic to string fields that can be localized
    const localizableFields: (keyof SurveyQuestionLocalization)[] = ['title', 'info'];
    const isLocalizable = localizableFields.includes(field as keyof SurveyQuestionLocalization);
    
    return {
      field: field, 
      headerName: headerName + (maxLength ? ` (${maxLength} chars)` : ''), 
      width: width, 
      flex : 1,
      editable: true,
      preProcessEditCellProps: (params) => {
        const value = params.props.value
        if (maxLength && value.length > maxLength) {
          // show error toast message
          return { ...params.props, error: true };
        }
        return { ...params.props, error: false };
      },
      valueGetter: isLocalizable ? (params: any, row : APIData<'api::survey-question.survey-question'>) => {
        return getLocaleValue(row, field as keyof SurveyQuestionLocalization, selectedLocale);
      } : undefined,
      valueSetter: isLocalizable ? (params: any, row: APIData<'api::survey-question.survey-question'>) => {
        return setLocaleValue(row, field as keyof SurveyQuestionLocalization, params, selectedLocale);
      } : undefined,
      renderCell: (params) => (
        <Typography variant="body2" 
          style={{ marginTop: '5px', whiteSpace: 'normal', wordBreak: 'break-word', color: 'inherit' }}>
          {isLocalizable ? getLocaleValue(params.row, field as keyof SurveyQuestionLocalization, selectedLocale) : params.value}
        </Typography>
      ),
      renderEditCell: (params : GridRenderEditCellParams<APIData<'api::survey-question.survey-question'>>) => (
        <Textarea
          style={{ 
            border: 'none', 
            width: "100%", 
            fontSize: "14px", 
            fontWeight: 400, 
            lineHeight: "22px",
            whiteSpace: 'normal', 
            wordBreak: 'break-word',
            padding: '8px',
            borderRadius: '4px',
            color: 'inherit',
            backgroundColor: 'inherit',
          }}
          value={params.value || ''}
          onChange={(event) => {
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: event.target.value
            });
          }}
          autoFocus
          onClick={(e) => e.stopPropagation()}
        />
      )
    }
  }

  const columns: GridColDef[] = [
    getTextColumn('title', 'Question Text', 450, tableState.selectedLocale),
    {
      field: 'type',
      headerName: 'Type',
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2"
          sx={{ 
            display: 'flex', 
            height: '100%', 
            alignItems: 'center',
            color: 'inherit',
          }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2"
          sx={{ 
            display: 'flex', 
            height: '100%', 
            alignItems: 'center',
            color: 'inherit',
          }}>
          {categories.find(c => c.categoryId === params.value)?.label || params.value}
        </Typography>
      )
    },
    {
      field: 'actions',
      type: 'actions',
      width: 240,
      headerName: 'Actions',
      align: 'left',
      headerAlign: 'left',
      getActions : ({id, row}) => {
        
        const workflowState = VerificationWorkflowUtils.getNextWorkflowState(rc, row.verificationStatus, reviewWorkflow)
        const hasUnsavedChanges = unsavedRows[id] !== undefined
        
        // Check if each action is allowed by workflow
        const isDiscardAllowed = VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.DISCARD_UPDATE, workflowState)
        const isSaveAllowed = VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.SAVE_UPDATE, workflowState)
        const isPublishAllowed = VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.PUBLISH, workflowState)
        const isUnpublishAllowed = VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.UNPUBLISH, workflowState)
        const isDeleteAllowed = VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.DELETE, workflowState)
        
        return [
          <Tooltip title="Edit">
            <span>
              <GridActionsCellItem
                icon={<EditIcon size={18} />}
                label="Edit"
                disabled={!isSaveAllowed}
                onClick={(event) => editQuestion(event, row)}
              />
            </span>
          </Tooltip>,
          <Tooltip title="Discard">
            <span>
              <GridActionsCellItem
                icon={<RestoreIcon />}
                label="Discard"
                disabled={!isDiscardAllowed || !hasUnsavedChanges}
                onClick={() => discardUnsavedRow(id)}
              />
            </span>
          </Tooltip>,
          <Tooltip title="Save">
            <span>
              <GridActionsCellItem
                icon={<SaveIcon />}
                label="Save"
                disabled={!isSaveAllowed || !hasUnsavedChanges}
                onClick={() => saveQuestions([unsavedRows[id]])}
              />
            </span>
          </Tooltip>,
          <Tooltip title="Publish">
            <span>
              <GridActionsCellItem
              icon={<PublishIcon />}
              label="Publish"
              disabled={!isPublishAllowed || row.editStatus === CONTENT_EDIT_STATUS.PUBLISHED || hasUnsavedChanges}
              onClick={() => publishQuestions([row.documentId])}
            />
            </span>
          </Tooltip>,
          <GridActionsCellItem
            icon={<FileDownloadOffIcon sx={{ transform: 'rotate(180deg)' }} />}
            label="Unpublish"
            showInMenu
            disabled={!isUnpublishAllowed || row.disabled || row.editStatus !== CONTENT_EDIT_STATUS.PUBLISHED || hasUnsavedChanges}
            onClick={() => unpublishQuestions([row.documentId])}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label={`${row.editStatus === CONTENT_EDIT_STATUS.DRAFT ? 'Delete draft' : 'Discard modification'}`} 
            showInMenu
            disabled={!isDeleteAllowed || row.editStatus === CONTENT_EDIT_STATUS.PUBLISHED || hasUnsavedChanges}
            onClick={() => discardAllDrafts([row.documentId])}
          />
        ];
      }
    },
    {
      field : 'verificationStatus',
      headerName : 'QA Status',
      width : 160,
      type : 'singleSelect',
      filterOperators : [valueToArrayFilterOperator()],
      renderHeaderFilter: (props : GridHeaderFilterCellProps) => {
        return <MultiChoiceFilterComponent 
                  {...props} 
                  options={Object.values(CONTENT_VERIFICATION_STATUS)} />
      },
      valueOptions : ({ id, row }) => {
        if (!id || !row) return []

        // Dont allow to change the QA status if there are unsaved changes or 
        // if the recommendation is published
        if (unsavedRows[id] !== undefined || row.editStatus === CONTENT_EDIT_STATUS.PUBLISHED) {
          return [row.verificationStatus]
        }
        
        const workflowState = VerificationWorkflowUtils.getNextWorkflowState(rc, row.verificationStatus, reviewWorkflow)
        const allowedTransitions = workflowState.nextQaStatus
        return [row.verificationStatus, ...allowedTransitions]
      },
      editable : true,
      renderCell : (params : GridRenderCellParams) => {
        return <div>{params.value}</div>
      }
    },
    { 
      field: 'editStatus', 
      headerName: 'Status', 
      width: 150,
      filterOperators : [valueToArrayFilterOperator()],
      renderCell: (params: GridRenderCellParams) => (
        <div style={{...styles.chipsContainerCellNoScroll as CSSProperties}}>
          {
            params.value === 'DRAFT' ? (
              <Chip label="Draft" 
                  sx={{ 
                    ...chipStyles.status.draft, 
                    borderRadius: '5px' 
                  }} 
                  size="small" variant="outlined" />
            ) : params.value === 'MODIFIED' ? (
              <Chip label="Modified" 
                  sx={{ 
                    mr: 0.5, 
                    ...chipStyles.status.modified, 
                    borderRadius: '5px'  
                  }} 
                  size="small" variant="outlined" />
            ) : (
              <Chip label="Published" 
                  sx={{ 
                    mr: 0.5, 
                    ...chipStyles.status.published, 
                    borderRadius: '5px'  
                  }} 
                  size="small" variant="outlined" />
            )
          }
        </div>      
      ),
      renderHeaderFilter: (props : GridHeaderFilterCellProps) => {
        return <SingleChoiceFilterComponent 
                  {...props} 
                  options={[...Object.values(CONTENT_EDIT_STATUS)]} />
      }
    },
  ]

  return (

    <div style={{ display: 'flex', flexDirection: 'column', paddingTop: '10px', 
      height: '100%', overflowY: 'auto'}}> 

      <Card style={{ padding: '15px', marginBottom: '20px', overflow: 'unset' }}>  
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center' }}>
              <LucideLibrary size={24} style={{ stroke: theme.palette.primary.main, marginRight: 10 }} />
              Question Bank
            </Typography>
            <Typography variant="subtitle2" color="text.primary">
              A repository of all questions available for building surveys.
            </Typography>
          </Box>
          <Box> 
            <BglPrimarySmallButton
              text="Add Question"
              onClick={addNewQuestion}
              sx={{ marginLeft: '10px', width: '150px' }}>
                <CirclePlus size={16} /> New Question
            </BglPrimarySmallButton>
          </Box>
        </Box>
      </Card>

      <Box sx={{ height: '100%', paddingRight: '20px' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>

          <div style={{ marginBottom: 8, display: 'flex', gap: 10 }}>

            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyUnsaved}
                  onChange={handleToggleUnsavedOnly}
                  disabled={!hasUnsavedRows || isSaving}
                  size="small"
                />
              }
              label="Show Unsaved"
              sx={{ 
                marginLeft: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '14px',
                  color: theme.palette.text.secondary
                }
              }}
            />
            
            {(() => {
              const selectedCount = selectedRows.length;
              const selectedUnsavedCount = selectedRows.filter(row => unsavedRows[row.id] !== undefined).length;
              const selectedPublishCandidates = selectedRows.filter(reco => {
                const workflowState = VerificationWorkflowUtils.getNextWorkflowState(rc, reco.verificationStatus, reviewWorkflow)
                return VerificationWorkflowUtils.isActionAllowed(CONTENT_EDIT_ACTION.PUBLISH, workflowState)
              });
              
              return (
                <>
                  <BglCloseOutlinedSmallButton
                    disabled={selectedCount === 0 || selectedUnsavedCount === 0 || isSaving}
                    onClick={discardSelectedUnsavedRows}
                    startIcon={<RestoreIcon />}>
                    {`Discard selected (${selectedUnsavedCount})`}
                  </BglCloseOutlinedSmallButton>
                  
                  <BglPrimaryOutlinedSmallButton
                    disabled={selectedCount === 0 || selectedUnsavedCount === 0 || isSaving}
                    onClick={saveSelectedUnsavedRows}
                    startIcon={<SaveIcon />}
                    loadingPosition="start">
                    {`Save selected (${selectedUnsavedCount})`}
                  </BglPrimaryOutlinedSmallButton>
                
                  <BglSecondaryOutlinedSmallButton
                    onClick={publishSelectedRows}
                    disabled={selectedCount === 0 || selectedPublishCandidates.length === 0}
                    startIcon={<PublishIcon />}>
                    {`Publish selected (${selectedPublishCandidates.length})`}
                  </BglSecondaryOutlinedSmallButton>
                </>
              )
            })()}

            {tableState.filters.items.length > 0 && (
              <BglPrimaryOutlinedSmallButton
                onClick={clearFilters}
                startIcon={<TuneIcon />}>
                {`Clear filters (${tableState.filters.items.length})`}
              </BglPrimaryOutlinedSmallButton>
            )}

          </div>

          <div> 
            <FormControl size="small" sx={{ minWidth: 100, ml: 2 }}>
              <InputLabel sx={{ fontSize: '14px', color: theme.palette.text.secondary }}>Locale</InputLabel>
              <Select
                sx={{ 
                  height: '36px', 
                  fontSize: '12px',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '.MuiSelect-select' : {
                    minHeight: 'unset',
                    color: theme.palette.text.secondary
                  }
                }}
                value={tableState.selectedLocale}
                onChange={(e) => handleLocaleChange(e.target.value)}
                label="Locale"
              >
                {(selectedPilot.supportedLocales as string[]).map((locale) => (
                  <MenuItem key={locale} value={locale} sx={{ color: theme.palette.text.secondary }}>
                    {locales.find(l => l.code === locale)?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

        </div>

        <DataGridPro
          checkboxSelection
          apiRef={apiRef}
          rowHeight={70}
          rows={memoizedRows}
          columns={columns}
          loading={isSaving}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          sx={getRecoTableStyles}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
            filter: {
              filterModel: {
                items: tableState.filters.items
              }
            }
          }}
          disableRowSelectionOnClick
          disableColumnFilter
          headerFilters
          slotProps={{
            headerFilterCell: {
              showClearIcon: true,
            },
          }}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={onProcessRowUpdateError}
          getRowClassName={getRowClassName}
          getCellClassName={getCellClassName}
          isCellEditable={isCellEditable}
          onFilterModelChange={handleFilterModelChange}
          onRowSelectionModelChange={handleSelectionChange}
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <UpsertSurveyQuestion
        mode={dialogMode}
        question={selectedQuestion}
        publishedQuestion={selectedPublishedQuestion}
        isOpen={dialogOpen}
        onClose={handleDialogClose}
        onMessage={setMessage}
      />
    
    </div>
  )
}